import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    Autocomplete,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
    getApiOrganizationSearchUsers,
    getApiOrganizationAddUser,
    getApiOrganizationRemoveUser,
} from "../../../../services/Admin/organizations";
import { listApi } from "../../../../services/Admin/request";

export default function OrganizationAssignedUsers({ organization_id, onClose, role_code, maxUserRoleWorkers, isSuperAdmin }) {
    // Estado con usuarios ya asignados a la organización
    const [assignedUsers, setAssignedUsers] = useState([]);
    // Estado con todos los usuarios disponibles (fuente general)
    const [allUsers, setAllUsers] = useState([]);
    // Usuario seleccionado en el selector para asignar
    const [selectedUser, setSelectedUser] = useState(null);
    // Cargas y flags
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Cargar usuarios asignados a la organización
    const fetchAssignedUsers = async () => {
        // Carga la lista de usuarios asignados a la organización actual
        setLoadingAssigned(true);
        try {
            const res = await getApiOrganizationSearchUsers(organization_id);
            // Suponemos que res.data es un array de usuarios { id, name, email, ... }
            let assigned = res?.data || [];
            if (Array.isArray(role_code) && role_code.length > 0) {
                assigned = assigned.filter((u) => role_code.includes(u.role));
            } else if (typeof role_code === 'string' && role_code) {
                assigned = assigned.filter((u) => u.role === role_code);
            }
            setAssignedUsers(assigned);
        } catch {
            setAssignedUsers([]);
        } finally {
            setLoadingAssigned(false);
        }
    };

    // Cargar todos los usuarios (fuente global, como en FICHERO 1)
    const fetchAllUsers = async () => {
        // Llama al endpoint general para listar usuarios
        setLoadingAll(true);
        try {
            const resp = await listApi("user");
            // En FICHERO 1: resp.data.data contenía el array de usuarios
            const data = resp?.data?.data || [];
            // Filtrar por role_code (puede ser string o array)
            let filtered = data;
            if (Array.isArray(role_code) && role_code.length > 0) {
                filtered = data.filter((u) => role_code.includes(u.role));
            } else if (typeof role_code === 'string' && role_code) {
                filtered = data.filter((u) => u.role === role_code);
            }
            // Normalizamos campos mínimos usados en UI
            const normalized = filtered.map((u) => ({
                id: u.id,
                name: u.name,
                email: u.email,
                isActive: u.isActive,
            }));
            setAllUsers(normalized);
        } catch (error) {
            setAllUsers([]);
            console.error("Error al cargar todos los usuarios:", error);
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchAssignedUsers();
    }, [organization_id]);

    useEffect(() => {
        fetchAllUsers();
    }, []);

    // Candidatos = todos los usuarios menos los ya asignados
    const candidateUsers = useMemo(() => {
        // Evita duplicados y oculta los ya asignados
        const assignedIds = new Set(assignedUsers.map((u) => u.id));
        return allUsers.filter((u) => u && !assignedIds.has(u.id));
    }, [allUsers, assignedUsers]);

    // Asignar usuario seleccionado
    const handleAssignUser = async () => {
        // Evita clics sin selección o durante asignación
        if (!selectedUser || assigning) return;
        setAssigning(true);
        // Optimismo: agregamos localmente antes de la respuesta
        const optimistic = [...assignedUsers, selectedUser];
        setAssignedUsers(optimistic);
        try {
            await getApiOrganizationAddUser({
                organization_id,
                user_id: selectedUser.id,
            });
            // Si la API va bien, limpiamos la selección
            setSelectedUser(null);
        } catch {
            // Si falla, revertimos el estado
            setAssignedUsers(assignedUsers);
        } finally {
            setAssigning(false);
        }
    };

    // Quitar usuario (unassign)
    const handleRemoveUser = async (userId) => {
        // Optimismo: quitamos localmente primero
        const prev = assignedUsers;
        setAssignedUsers(prev.filter((u) => u.id !== userId));
        try {
            await getApiOrganizationRemoveUser({
                organization_id,
                user_id: userId,
            });
        } catch {
            // Revertimos en caso de fallo
            setAssignedUsers(prev);
        }
    };

    return (
        <>
            {/* WORKERS LIST estilo chips sin iconos */}
            <Box sx={{
                border: "1px solid #e0e0e0",
                p: 2,
                borderRadius: 2,
                bgcolor: "#fafafa",
                maxHeight: 220,
                overflowY: "auto",
                mb: 2
            }}>
                {loadingAssigned ? (
                    <CircularProgress size={20} />
                ) : assignedUsers && assignedUsers.length > 0 ? (
                    assignedUsers.map((user) => (
                        <Chip
                            key={user.id}
                            label={`${user.name}  |  ${user.email}`}
                            sx={{
                                bgcolor: "#e7effa",
                                fontSize: "0.85em",
                                mb: 1
                            }}
                            onDelete={() => handleRemoveUser(user.id)}
                            onClick={() => {
                                if (navigator && navigator.clipboard) {
                                    navigator.clipboard.writeText(user.email);
                                    setSnackbarOpen(true);
                                }
                            }}
                        />
                    ))
                ) : (
                    <Typography textAlign="center" color="text.secondary">
                        No hay usuarios asignados.
                    </Typography>
                )}
            </Box>

            {isSuperAdmin ? (
                <>
                    {/* Selector + Botón asignar sin iconos */}
                    <Box display="flex" gap={1} alignItems="center">
                        {maxUserRoleWorkers <= assignedUsers.length && role_code === "ROLE_WORKER" ? (
                            <Typography color="error" variant="body2">
                                Límite de {maxUserRoleWorkers} usuarios alcanzado para este rol.
                            </Typography>
                        ) : (
                            <>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={candidateUsers}
                                    value={selectedUser}
                                    onChange={(_, value) => setSelectedUser(value)}
                                    loading={loadingAll}
                                    getOptionLabel={(option) =>
                                        option ? `${option.name ?? ""} (${option.email ?? ""})` : ""
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar usuario…"
                                            placeholder="Nombre o email"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingAll ? (
                                                            <CircularProgress size={16} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    noOptionsText={
                                        loadingAll ? "Cargando…" : "Sin coincidencias disponibles"
                                    }
                                />

                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleAssignUser}
                                    disabled={!selectedUser || assigning}
                                >
                                    {assigning ? "Asignando…" : "Asignar"}
                                </Button>
                            </>
                        )}
                    </Box>
                </>
            ) : null}

            {/* SNACKBAR para feedback al copiar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={1800}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success">Correo copiado!</Alert>
            </Snackbar>
        </>
    );
}